import React, { useEffect, useState, createElement } from 'react';
import { Download, RefreshCw, Filter, X, ChevronRight, Search, Clock, ShieldAlert, CheckCircle2, Loader2 } from 'lucide-react';
import { Card, Button, StatusPill, SystemBadge, Input } from '../components/ui/Primitives';
import { mockEvents } from '../components/data/mockData';
export function EventLog() {
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>(['Source: Bonzo', 'Result: Error']);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [drawerTab, setDrawerTab] = useState('Overview');
  const [actionStatus, setActionStatus] = useState<string | null>(null);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedEvent(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);
  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => {
      setRefreshing(false);
      setLastRefreshed(new Date());
    }, 1000);
  };
  const handleExport = () => {
    const headers = ['Timestamp', 'Event ID', 'Source', 'Target', 'Type', 'Phone', 'Status', 'Duration (ms)'];
    const csvContent = [headers.join(','), ...filteredEvents.map(e => [e.timestamp, e.id, e.source, e.target, e.type, e.phone, e.status, e.duration].join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `event_log_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  const handleDrawerAction = (action: string) => {
    setActionStatus(action);
    setTimeout(() => setActionStatus(null), 2000);
  };
  const filteredEvents = mockEvents.filter(e => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!e.phone.toLowerCase().includes(q) && !e.id.toLowerCase().includes(q) && !e.prospectId.toLowerCase().includes(q)) {
        return false;
      }
    }
    if (activeFilters.includes('Source: Bonzo') && e.source !== 'bonzo') return false;
    if (activeFilters.includes('Result: Error') && e.status !== 'error') return false;
    return true;
  });
  return <div className="h-full flex flex-col relative" data-id="element-331">
      <div className="flex items-center justify-between mb-4 shrink-0" data-id="element-332">
        <div data-id="element-333">
          <h1 className="text-xl font-semibold text-slate-900" data-id="element-334">Event Log</h1>
          <p className="text-sm text-slate-500" data-id="element-335">
            {filteredEvents.length} events today • Last refreshed{' '}
            {lastRefreshed.toLocaleTimeString()}
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-336">
          <Button variant="outline" size="sm" onClick={handleExport} data-id="element-337">
            <Download className="w-4 h-4 mr-2" data-id="element-338" /> Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} data-id="element-339">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} data-id="element-340" />{' '}
            Refresh
          </Button>
        </div>
      </div>

      <Card className="mb-4 p-3 shrink-0 flex flex-wrap items-center gap-3 sticky top-0 z-10" data-id="element-341">
        <div className="relative w-64" data-id="element-342">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-343" />
          <Input placeholder="Search phone, ID, payload..." className="pl-9 h-8 text-xs" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-344" />
        </div>
        <div className="h-4 w-px bg-slate-200 mx-1" data-id="element-345" />
        {activeFilters.map(f => <FilterChip key={f} label={f} onRemove={() => setActiveFilters(prev => prev.filter(x => x !== f))} data-id="element-346" />)}
        <div className="relative ml-auto" data-id="element-347">
          <Button variant="ghost" size="sm" className="h-8 text-xs text-electric-600 hover:text-electric-700 hover:bg-electric-50" onClick={() => setShowFilterMenu(!showFilterMenu)} data-id="element-348">
            <Filter className="w-3 h-3 mr-1.5" data-id="element-349" /> Add Filter
          </Button>
          {showFilterMenu && <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50" data-id="element-350">
              {['Source: Bonzo', 'Result: Error'].map(f => <button key={f} className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={() => {
            if (!activeFilters.includes(f)) setActiveFilters([...activeFilters, f]);
            setShowFilterMenu(false);
          }} data-id="element-351">
                  {f}
                </button>)}
            </div>}
        </div>
      </Card>

      <Card className="flex-1 overflow-hidden flex flex-col min-h-[400px]" data-id="element-352">
        <div className="overflow-x-auto flex-1" data-id="element-353">
          <table className="w-full text-left border-collapse" data-id="element-354">
            <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200" data-id="element-355">
              <tr data-id="element-356">
                <th className="px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider w-10" data-id="element-357"></th>
                <th className="px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-358">
                  Timestamp
                </th>
                <th className="px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-359">
                  Event ID
                </th>
                <th className="px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-360">
                  Direction
                </th>
                <th className="px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-361">
                  Type
                </th>
                <th className="px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-362">
                  Phone
                </th>
                <th className="px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-363">
                  Result
                </th>
                <th className="px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider text-right" data-id="element-364">
                  Duration
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-365">
              {filteredEvents.map(event => <tr key={event.id} onClick={() => {
              setSelectedEvent(event);
              setDrawerTab('Overview');
            }} className={`hover:bg-electric-50/50 cursor-pointer transition-colors ${selectedEvent?.id === event.id ? 'bg-electric-50/50' : ''}`} data-id="element-366">
                  <td className="px-4 py-2" data-id="element-367">
                    <div className={`w-1 h-4 rounded-full ${event.status === 'error' ? 'bg-red-500' : event.status === 'synced' ? 'bg-emerald-500' : 'bg-amber-500'}`} data-id="element-368" />
                  </td>
                  <td className="px-4 py-2 text-xs font-mono text-slate-600 whitespace-nowrap" data-id="element-369">
                    {new Date(event.timestamp).toLocaleString([], {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })}
                  </td>
                  <td className="px-4 py-2 text-xs font-mono text-slate-500" data-id="element-370">
                    {event.id}
                  </td>
                  <td className="px-4 py-2" data-id="element-371">
                    <div className="flex items-center gap-1.5" data-id="element-372">
                      <SystemBadge system={event.source as any} showLabel={false} data-id="element-373" />
                      <ChevronRight className="w-3 h-3 text-slate-300" data-id="element-374" />
                      <SystemBadge system={event.target as any} showLabel={false} data-id="element-375" />
                    </div>
                  </td>
                  <td className="px-4 py-2 text-sm text-slate-700" data-id="element-376">
                    {event.type}
                  </td>
                  <td className="px-4 py-2 text-xs font-mono text-slate-700" data-id="element-377">
                    {event.phone}
                  </td>
                  <td className="px-4 py-2" data-id="element-378">
                    <StatusPill status={event.status as any} data-id="element-379" />
                  </td>
                  <td className="px-4 py-2 text-xs font-mono text-slate-500 text-right" data-id="element-380">
                    {event.duration}ms
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Drawer Overlay */}
      {selectedEvent && <div className="absolute inset-0 z-20 flex justify-end" data-id="element-381">
          <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm" onClick={() => setSelectedEvent(null)} data-id="element-382" />
          <div className="w-[600px] bg-surface h-full shadow-2xl flex flex-col animate-in slide-in-from-right border-l border-slate-200 relative z-30" data-id="element-383">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50" data-id="element-384">
              <div data-id="element-385">
                <h2 className="text-lg font-semibold text-slate-900" data-id="element-386">
                  Event Details
                </h2>
                <p className="text-xs font-mono text-slate-500 mt-1" data-id="element-387">
                  {selectedEvent.id}
                </p>
              </div>
              <button onClick={() => setSelectedEvent(null)} className="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-200 transition-colors" data-id="element-388">
                <X className="w-5 h-5" data-id="element-389" />
              </button>
            </div>

            <div className="flex border-b border-slate-200 px-4" data-id="element-390">
              {['Overview', 'Payload', 'Timeline', 'Diagnostics'].map(tab => <button key={tab} onClick={() => setDrawerTab(tab)} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${tab === drawerTab ? 'border-electric-500 text-electric-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-391">
                  {tab}
                </button>)}
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-6" data-id="element-392">
              {drawerTab === 'Overview' && <>
                  <div className="grid grid-cols-2 gap-4" data-id="element-393">
                    <DetailItem label="Status" value={<StatusPill status={selectedEvent.status as any} data-id="element-395" />} data-id="element-394" />
                    <DetailItem label="Timestamp" value={<span className="font-mono text-sm" data-id="element-397">
                          {new Date(selectedEvent.timestamp).toLocaleString()}
                        </span>} data-id="element-396" />
                    <DetailItem label="Phone" value={<span className="font-mono text-sm text-electric-600" data-id="element-399">
                          {selectedEvent.phone}
                        </span>} data-id="element-398" />
                    <DetailItem label="Prospect ID" value={<span className="font-mono text-sm" data-id="element-401">
                          {selectedEvent.prospectId}
                        </span>} data-id="element-400" />
                  </div>

                  <Card className="p-4 bg-slate-50 border-dashed" data-id="element-402">
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-403">
                      Systems Touched
                    </h3>
                    <div className="flex items-center gap-3" data-id="element-404">
                      <SystemBadge system={selectedEvent.source as any} data-id="element-405" />
                      <div className="h-px w-8 bg-slate-300" data-id="element-406" />
                      <SystemBadge system={selectedEvent.target as any} data-id="element-407" />
                    </div>
                  </Card>
                </>}

              {drawerTab === 'Payload' && <div data-id="element-408">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-409">
                    Webhook Payload
                  </h3>
                  <pre className="bg-slate-900 text-slate-300 p-4 rounded-lg text-xs font-mono overflow-x-auto" data-id="element-410">
                    {JSON.stringify({
                event_id: selectedEvent.id,
                type: selectedEvent.type,
                data: {
                  phone: selectedEvent.phone,
                  prospect_id: selectedEvent.prospectId,
                  dnc_status: true,
                  source_system: selectedEvent.source
                },
                timestamp: selectedEvent.timestamp
              }, null, 2)}
                  </pre>
                </div>}

              {drawerTab === 'Timeline' && <div data-id="element-411">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-412">
                    Execution Timeline
                  </h3>
                  <div className="space-y-4 relative before:absolute before:inset-0 before:ml-2.5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent" data-id="element-413">
                    <TimelineItem icon={<Clock className="w-3 h-3 text-slate-500" data-id="element-415" />} title="Webhook Received" time="0ms" data-id="element-414" />
                    <TimelineItem icon={<ShieldAlert className="w-3 h-3 text-emerald-500" data-id="element-417" />} title="Loop Guard Check: PASSED" time="+12ms" data-id="element-416" />
                    <TimelineItem icon={<CheckCircle2 className="w-3 h-3 text-emerald-500" data-id="element-419" />} title="API Request Sent" time="+45ms" data-id="element-418" />
                    <TimelineItem icon={<div className={`w-2 h-2 rounded-full ${selectedEvent.status === 'error' ? 'bg-red-500' : 'bg-emerald-500'}`} data-id="element-421" />} title={selectedEvent.status === 'error' ? 'API Error Response' : 'API Success Response'} time={`+${selectedEvent.duration}ms`} isLast data-id="element-420" />
                  </div>
                </div>}

              {drawerTab === 'Diagnostics' && <div className="space-y-4" data-id="element-422">
                  <DetailItem label="Loop Guard Hash" value={<span className="font-mono text-sm" data-id="element-424">lg_8f72a9bc3d</span>} data-id="element-423" />
                  <DetailItem label="Retry Count" value={<span className="font-mono text-sm" data-id="element-426">0</span>} data-id="element-425" />
                  {selectedEvent.status === 'error' && <div data-id="element-427">
                      <div className="text-xs text-slate-500 mb-1" data-id="element-428">
                        Error Stack
                      </div>
                      <pre className="bg-red-50 text-red-800 p-3 rounded border border-red-100 text-xs font-mono overflow-x-auto" data-id="element-429">
                        Error: 429 Rate limit exceeded{'\n'}
                        at ApiClient.request (/lib/api.js:42:15){'\n'}
                        at SyncJob.run (/lib/sync.js:118:22)
                      </pre>
                    </div>}
                </div>}
            </div>

            <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2" data-id="element-430">
              <Button variant="outline" onClick={() => handleDrawerAction('ignored')} data-id="element-431">
                {actionStatus === 'ignored' ? 'Ignored ✓' : 'Mark Ignored'}
              </Button>
              <Button variant="primary" onClick={() => handleDrawerAction('retry')} data-id="element-432">
                {actionStatus === 'retry' ? 'Queued ✓' : 'Retry Sync'}
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}
function FilterChip({
  label,
  onRemove
}: {
  label: string;
  onRemove: () => void;
}) {
  return <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 border border-slate-200 rounded-md text-xs font-medium text-slate-700" data-id="element-433">
      {label}
      <button className="text-slate-400 hover:text-slate-600" onClick={onRemove} data-id="element-434">
        <X className="w-3 h-3" data-id="element-435" />
      </button>
    </div>;
}
function DetailItem({
  label,
  value
}: {
  label: string;
  value: React.ReactNode;
}) {
  return <div data-id="element-436">
      <div className="text-xs text-slate-500 mb-1" data-id="element-437">{label}</div>
      <div className="text-sm font-medium text-slate-900" data-id="element-438">{value}</div>
    </div>;
}
function TimelineItem({
  icon,
  title,
  time,
  isLast
}: any) {
  return <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-439">
      <div className="flex items-center justify-center w-6 h-6 rounded-full border border-white bg-slate-100 text-slate-500 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-440">
        {icon}
      </div>
      <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-3 rounded-lg border border-slate-200 bg-white shadow-sm flex items-center justify-between" data-id="element-441">
        <span className="text-sm font-medium text-slate-800" data-id="element-442">{title}</span>
        <span className="text-xs font-mono text-slate-400" data-id="element-443">{time}</span>
      </div>
    </div>;
}